import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Package,
  Truck,
  Wheat,
  Store,
  Mic,
  ClipboardList,
  Radio,
  Activity,
  FileText,
  Camera,
  Factory,
  BarChart3,
  DollarSign,
  FlaskConical,
  Route,
  FileStack,
  Shield,
  ScrollText,
  HeartPulse,
  LayoutGrid,
  BookOpen,
  DatabaseZap,
  FileBarChart,
  ChevronDown,
  ChevronRight,
  Star,
  Search,
  Pin,
  Wrench,
  Brain,
  Zap,
  Users,
  ShoppingCart,
  UserCog,
  Table2,
  LayoutTemplate,
} from "lucide-react";
import { useUser } from "../contexts/UserContext";

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  module?: string;
  badge?:
    | "inventory-alerts"
    | "paperless-pending"
    | "production-pending"
    | "maintenance-high"
    | "crm-pending"
    | "iot-offline";
}

interface SectionDef {
  key: string;
  label: string;
  emoji: string;
  items: NavItem[];
}

const SECTIONS: SectionDef[] = [
  {
    key: "factory",
    label: "工廠管理",
    emoji: "🏭",
    items: [
      { to: "/production", label: "生管排程", icon: Factory, module: "production", badge: "production-pending" },
      { to: "/inventory", label: "庫存產能", icon: BarChart3, module: "inventory", badge: "inventory-alerts" },
      { to: "/maintenance", label: "預測維護", icon: Wrench, module: "maintenance", badge: "maintenance-high" },
      { to: "/warroom", label: "戰情室", icon: Activity, module: "warroom" },
      { to: "/cameras", label: "攝影機", icon: Camera, module: "cameras" },
      { to: "/iot-hub", label: "IoT 整合", icon: Radio, badge: "iot-offline" },
    ],
  },
  {
    key: "rnd",
    label: "產品研發",
    emoji: "🔬",
    items: [
      { to: "/bom", label: "BOM 管理", icon: ClipboardList, module: "bom" },
      { to: "/recipe-kb", label: "配方知識庫", icon: FlaskConical, module: "recipe-kb" },
      { to: "/traceability", label: "食品追溯", icon: Route, module: "traceability" },
      { to: "/ocr", label: "手寫辨識", icon: FileText, module: "ocr" },
    ],
  },
  {
    key: "business",
    label: "業務相關",
    emoji: "💼",
    items: [
      { to: "/products", label: "產品主檔", icon: Package, module: "products" },
      { to: "/suppliers", label: "供應商", icon: Truck, module: "suppliers" },
      { to: "/ingredients", label: "原料主檔", icon: Wheat, module: "ingredients" },
      { to: "/stores", label: "通路管理", icon: Store, module: "stores" },
      { to: "/pricing", label: "動態報價", icon: DollarSign, module: "pricing" },
      { to: "/crm", label: "客戶管理", icon: Users, module: "crm", badge: "crm-pending" },
      { to: "/procurement", label: "採購管理", icon: ShoppingCart, module: "procurement" },
    ],
  },
  {
    key: "admin",
    label: "行政作業",
    emoji: "📋",
    items: [
      { to: "/audio2sql", label: "語音分析", icon: Mic, module: "audio2sql" },
      { to: "/paperless", label: "無紙化", icon: FileStack, module: "paperless", badge: "paperless-pending" },
      { to: "/hr", label: "人力資源", icon: UserCog, module: "hr" },
      { to: "/automation", label: "自動化規則", icon: Zap },
      { to: "/audit", label: "操作紀錄", icon: ScrollText, module: "audit" },
      { to: "/admin", label: "權限管理", icon: Shield, module: "admin" },
    ],
  },
  {
    key: "analytics",
    label: "報表分析",
    emoji: "📊",
    items: [
      { to: "/reports", label: "報表中心", icon: FileBarChart },
      { to: "/builder", label: "自訂看板", icon: LayoutGrid },
      { to: "/datasource", label: "資料整合", icon: DatabaseZap },
      { to: "/integrations", label: "整合文件", icon: BookOpen },
      { to: "/templates", label: "範本市集", icon: LayoutTemplate },
      { to: "/custom-tables", label: "自訂資料表", icon: Table2 },
      { to: "/ai-ops", label: "AI 營運", icon: Brain, module: "admin" },
      { to: "/status", label: "系統狀態", icon: HeartPulse },
    ],
  },
];

const ALL_ITEMS: NavItem[] = SECTIONS.flatMap((s) => s.items);

const LS_FAVORITES = "qimei-sidebar-favorites";
const LS_COLLAPSED = "qimei-sidebar-collapsed";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function useBadgeCount(url: string, countKey: string, intervalMs = 60_000) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          if (typeof data === "number") setCount(data);
          else if (typeof data?.count === "number") setCount(data.count);
          else if (Array.isArray(data)) setCount(data.filter((d: { acknowledged?: boolean }) => !d[countKey as keyof typeof d]).length);
        }
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, intervalMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [url, countKey, intervalMs]);

  return count;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useBadgeCountFn(url: string, countFn: (data: any) => number, intervalMs = 60_000) {
  const [count, setCount] = useState(0);
  const countFnRef = useRef(countFn);
  countFnRef.current = countFn;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setCount(countFnRef.current(data));
        }
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, intervalMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [url, intervalMs]);

  return count;
}

export function Sidebar() {
  const location = useLocation();
  const { user, loading } = useUser();

  const [favorites, setFavorites] = useState<string[]>(() => readJson(LS_FAVORITES, []));
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() =>
    readJson(LS_COLLAPSED, { rnd: true, business: true, admin: true, analytics: true }),
  );
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const inventoryAlerts = useBadgeCount("/api/inventory/alerts", "acknowledged");
  const paperlessPending = useBadgeCount("/api/paperless/pending", "count");

  const productionPending = useBadgeCountFn(
    "/api/production/orders",
    (data) => Array.isArray(data) ? data.filter((o: { status: string }) => o.status === "pending").length : 0,
  );
  const maintenanceHigh = useBadgeCountFn(
    "/api/maintenance/predictions",
    (data) => Array.isArray(data) ? data.filter((p: { probability: number }) => p.probability >= 0.7).length : 0,
  );
  const crmPending = useBadgeCountFn(
    "/api/crm/orders",
    (data) => {
      const items = Array.isArray(data) ? data : data?.items ?? [];
      return items.filter((o: { status: string }) => o.status === "pending").length;
    },
  );
  const iotOffline = useBadgeCountFn(
    "/api/iot/overview",
    (data) => {
      if (data && typeof data.devices_total === "number" && typeof data.devices_online === "number") {
        return Math.max(0, data.devices_total - data.devices_online);
      }
      return 0;
    },
  );

  const badgeCounts: Record<string, number> = {
    "inventory-alerts": inventoryAlerts,
    "paperless-pending": paperlessPending,
    "production-pending": productionPending,
    "maintenance-high": maintenanceHigh,
    "crm-pending": crmPending,
    "iot-offline": iotOffline,
  };

  useEffect(() => {
    localStorage.setItem(LS_FAVORITES, JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    localStorage.setItem(LS_COLLAPSED, JSON.stringify(collapsed));
  }, [collapsed]);

  const allowed = user?.allowed_modules ?? [];
  const isAdmin = user?.role === "admin";

  const canAccess = useCallback(
    (module?: string) => {
      if (!user || loading) return true;
      if (isAdmin) return true;
      if (!module) return true;
      return allowed.includes(module);
    },
    [user, loading, isAdmin, allowed],
  );

  const toggleFavorite = useCallback((to: string) => {
    setFavorites((prev) =>
      prev.includes(to) ? prev.filter((f) => f !== to) : [...prev, to],
    );
  }, []);

  const toggleSection = useCallback((key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const isActive = useCallback(
    (to: string) =>
      to === "/" ? location.pathname === "/" : location.pathname.startsWith(to),
    [location.pathname],
  );

  const searchLower = search.toLowerCase().trim();

  const filteredSections = useMemo(() => {
    return SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (!canAccess(item.module)) return false;
        if (item.module === "admin" && !isAdmin) return false;
        if (item.module === "audit" && !isAdmin) return false;
        if (searchLower && !item.label.toLowerCase().includes(searchLower)) return false;
        return true;
      }),
    })).filter((s) => s.items.length > 0);
  }, [canAccess, isAdmin, searchLower]);

  const favoriteItems = useMemo(() => {
    return favorites
      .map((to) => ALL_ITEMS.find((item) => item.to === to))
      .filter((item): item is NavItem => !!item && canAccess(item.module));
  }, [favorites, canAccess]);

  const renderItem = (item: NavItem, showStar: boolean) => {
    const active = isActive(item.to);
    const isFav = favorites.includes(item.to);
    const badge = item.badge ? badgeCounts[item.badge] : 0;

    return (
      <div key={item.to} className="group relative flex items-center">
        <Link
          to={item.to}
          className={`flex items-center gap-2.5 w-full pl-3 pr-2 text-[13px] font-medium transition-colors no-underline select-none ${
            active
              ? "text-qimei-600 border-l-[3px] border-qimei-500 pl-[9px]"
              : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-l-[3px] border-transparent pl-[9px]"
          }`}
          style={{ height: 36 }}
        >
          <item.icon size={16} strokeWidth={active ? 2 : 1.5} className="shrink-0" />
          <span className="truncate">{item.label}</span>
          {badge > 0 && (
            <span className="ml-auto shrink-0 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none px-1">
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </Link>
        {showStar && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleFavorite(item.to);
            }}
            className={`absolute right-1.5 p-0.5 rounded transition-opacity ${
              isFav
                ? "opacity-100"
                : "opacity-0 group-hover:opacity-100"
            }`}
            aria-label={isFav ? "取消收藏" : "加入收藏"}
          >
            <Star
              size={13}
              className={isFav ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-400"}
            />
          </button>
        )}
      </div>
    );
  };

  const renderSectionHeader = (key: string, label: string, emoji: string) => {
    const isCollapsed = !!collapsed[key];
    return (
      <button
        type="button"
        onClick={() => toggleSection(key)}
        className="flex items-center gap-1.5 w-full px-3 pt-3 pb-1 cursor-pointer select-none group/header"
      >
        {isCollapsed ? (
          <ChevronRight size={12} className="text-slate-400" />
        ) : (
          <ChevronDown size={12} className="text-slate-400" />
        )}
        <span className="text-[10px] uppercase tracking-widest text-slate-400 group-hover/header:text-slate-600 transition-colors">
          {emoji} {label}
        </span>
      </button>
    );
  };

  const isSearching = searchLower.length > 0;

  return (
    <aside className="w-[220px] border-r border-slate-200 bg-white shrink-0 hidden md:flex flex-col overflow-y-auto">
      {/* Search */}
      <div className="px-3 pt-3 pb-1">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋功能..."
            className="w-full h-8 pl-8 pr-3 rounded-md border border-slate-200 bg-slate-50 text-[13px] text-slate-700 placeholder:text-slate-400 outline-none focus:border-qimei-300 focus:ring-1 focus:ring-qimei-200 transition-colors"
          />
        </div>
      </div>

      <nav className="flex-1 py-1 space-y-0 overflow-y-auto">
        {/* Flat search results */}
        {isSearching && (
          <div className="px-1 py-1">
            {filteredSections.flatMap((s) =>
              s.items.map((item) => renderItem(item, true)),
            )}
            {filteredSections.every((s) => s.items.length === 0) && (
              <div className="px-3 py-4 text-[12px] text-slate-400 text-center">
                找不到相關功能
              </div>
            )}
          </div>
        )}

        {/* Favorites section */}
        {!isSearching && (
          <>
            <div className="px-1">
              <button
                type="button"
                onClick={() => toggleSection("favorites")}
                className="flex items-center gap-1.5 w-full px-3 pt-3 pb-1 cursor-pointer select-none group/header"
              >
                {collapsed.favorites ? (
                  <ChevronRight size={12} className="text-slate-400" />
                ) : (
                  <ChevronDown size={12} className="text-slate-400" />
                )}
                <span className="text-[10px] uppercase tracking-widest text-slate-400 group-hover/header:text-slate-600 transition-colors">
                  📌 常用
                </span>
              </button>
              {!collapsed.favorites && (
                <div
                  className="overflow-hidden transition-all duration-200"
                >
                  {favoriteItems.length > 0 ? (
                    favoriteItems.map((item) => renderItem(item, true))
                  ) : (
                    <div className="px-3 py-2 text-[11px] text-slate-400 flex items-center gap-1.5">
                      <Pin size={11} className="text-slate-300" />
                      點擊 ☆ 收藏常用功能
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Grouped sections */}
            {filteredSections.map((section) => (
              <div key={section.key} className="px-1">
                {renderSectionHeader(section.key, section.label, section.emoji)}
                {!collapsed[section.key] && (
                  <div className="overflow-hidden transition-all duration-200">
                    {section.items.map((item) => renderItem(item, true))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </nav>

      {/* User info */}
      {user && (
        <div className="border-t border-slate-200 px-3 py-2.5 shrink-0">
          <div className="text-[13px] font-medium text-slate-700 truncate">{user.name}</div>
          <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-qimei-50 text-qimei-600">
            {user.role_label}
          </span>
        </div>
      )}
    </aside>
  );
}
